/**
 * Migration Note: SeedStatus
 *
 * Previous location: src/sections/seeds/SeedStatus.tsx
 * New location: src/sections/seeds/components/badges/SeedStatus.tsx
 * Migrated: 2025-10-22 (Phase 2)
 * Reason: Standardize component organization (badges subdirectory)
 *
 * Breaking changes: Import path updated
 *
 * Update: 2025-01 - Migrated from StatusBadge to StatusChip for consistent styling
 */

import {
  CheckCircleIcon,
  ClockIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import { stripLegacyPrefix } from '@/components/actions/constants/actionApplicability';
import { Frozen as FrozenIcon } from '@/components/chariot-ui';
import {
  StatusChip,
  StatusChipProps,
  StatusChipVariant,
} from '@/components/ui/status-chip';
import { Seed, SeedStatus, SeedStatusLabel } from '@/types';
import { cn } from '@/utils/classname';
import {
  getVariantForStatus,
  seedStatusVariants,
} from '@/utils/statusChip.util';

export function SeedStatusComponent(props: { seed: Seed; className?: string }) {
  const { seed, className } = props;

  const seedStatusProps = getSeedStatusProps(seed);

  if (!seedStatusProps) return null;

  return <StatusChip {...seedStatusProps} className={cn(className)} />;
}

interface SeedStatusChipProps extends StatusChipProps {
  variant: StatusChipVariant;
}

// eslint-disable-next-line react-refresh/only-export-components
export const getSeedStatusProps = (seed: Seed): SeedStatusChipProps | null => {
  if (!seed?.status) return null;

  // Normalize with the same rule as the action-gating classifiers so display
  // and gating never disagree on what a raw status string means.
  const cleanStatus = stripLegacyPrefix(seed.status) as SeedStatus;
  const label = SeedStatusLabel[cleanStatus] || cleanStatus;

  switch (cleanStatus) {
    case SeedStatus.Accept:
    case SeedStatus.AcceptLow:
      return {
        label: 'Approved', // Both map to "Approved"
        variant: getVariantForStatus('approved', seedStatusVariants),
        icon: <CheckCircleIcon className="size-3 shrink-0" />,
        tooltip: 'Seed has been approved for scanning',
      };

    case SeedStatus.Pending:
    case SeedStatus.PendingLow:
      return {
        label: 'Pending', // Both map to "Pending"
        variant: getVariantForStatus('pending', seedStatusVariants),
        icon: <ClockIcon className="size-3 shrink-0" />,
        tooltip: 'Seed is pending approval',
      };

    case SeedStatus.Reject:
      return {
        label: 'Rejected',
        variant: getVariantForStatus('rejected', seedStatusVariants),
        icon: <XCircleIcon className="size-3 shrink-0" />,
        tooltip: 'Seed has been rejected',
      };

    case SeedStatus.Freeze:
      return {
        label: 'Frozen',
        variant: getVariantForStatus('frozen', seedStatusVariants),
        icon: <FrozenIcon className="size-3 shrink-0" />,
        tooltip: 'Seed has been frozen',
      };

    case SeedStatus.Delete:
      return {
        label: 'Deleted',
        variant: getVariantForStatus('deleted', seedStatusVariants),
        icon: <TrashIcon className="size-3 shrink-0" />,
        tooltip: 'Seed has been deleted',
      };

    default:
      return {
        label,
        variant: 'muted',
        icon: null,
        tooltip: `Status: ${label}`,
      };
  }
};
